import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../Card';
import { ShareBreakdown } from './ShareBreakdown';
import type { BucketRow } from '../../types/traffic';
interface TechnicalBreakdownsProps {
  browsers: BucketRow[];
  languages: BucketRow[];
  connections: BucketRow[];
  mediums: BucketRow[];
  coverage: {
    browser: number;
    language: number;
    connection: number;
    medium: number;
  };
}
function toRows(rows: BucketRow[]) {
  return rows.map(row => ({
    label: row.label,
    share: row.share,
    detail: row.requests.toLocaleString()
  }));
}

/** States how much of the traffic the breakdown above actually describes. */
function Coverage({
  share,
  reason
}: {
  share: number;
  reason: string;
}) {
  const missing = Math.max(0, Math.round((1 - share) * 100));
  if (missing === 0) return null;
  return <p className="mt-4 border-t border-border pt-3 text-xs leading-relaxed text-muted-foreground" data-id="element-447" data-mp="363dmeyk9wpm5">
      {missing}% of views didn&rsquo;t report this — {reason}. Shares above cover the rest.
    </p>;
}
function EmptyHint() {
  return <p className="py-6 text-center font-mono text-xs text-muted-foreground" data-id="element-448" data-mp="1ggt002bhpnu8">No data yet</p>;
}
export function TechnicalBreakdowns({
  browsers,
  languages,
  connections,
  mediums,
  coverage
}: TechnicalBreakdownsProps) {
  return <section className="grid gap-4 sm:gap-6 lg:grid-cols-2 xl:grid-cols-4" data-id="element-449" data-mp="17etlbgny6vug">
      <Card data-id="element-450" data-mp="12ha7ghv5wl31">
        <CardHeader data-id="element-451" data-mp="12htntu55z1ey">
          <CardTitle data-id="element-452" data-mp="12ictabdmnxc3">Browser & OS</CardTitle>
          <CardDescription data-id="element-453" data-mp="xqbmv63550tz">What people are hitting the page with</CardDescription>
        </CardHeader>
        <CardContent data-id="element-454" data-mp="sqp98vjehikn">
          {browsers.length ? <ShareBreakdown rows={toRows(browsers)} data-id="element-455" data-mp="srqm95jz8f50" /> : <EmptyHint data-id="element-456" data-mp="nrvuz0wahnjf" />}
          <Coverage share={coverage.browser} reason="usually bots, in-app webviews, or clients with a stripped user agent" data-id="element-457" data-mp="nsdxgf9joxzs" />
        </CardContent>
      </Card>

      <Card data-id="element-458" data-mp="eqnwoitrwmpc">
        <CardHeader data-id="element-459" data-mp="eqxpee67d7yq">
          <CardTitle data-id="element-460" data-mp="esimaafzp2zr">Languages</CardTitle>
          <CardDescription data-id="element-461" data-mp="53lcqx2to1ux">Browser locale — useful for translating this page</CardDescription>
        </CardHeader>
        <CardContent data-id="element-462" data-mp="94x7ti4gtfai">
          {languages.length ? <ShareBreakdown rows={toRows(languages)} data-id="element-463" data-mp="96qe2wb61xhz" /> : <EmptyHint data-id="element-464" data-mp="3ras13slp8mr6" />}
          <Coverage share={coverage.language} reason="non-browser clients that send no Accept-Language" data-id="element-465" data-mp="3rbl90wwe9iaw" />
        </CardContent>
      </Card>

      <Card data-id="element-466" data-mp="nu300m4vy58w">
        <CardHeader data-id="element-467" data-mp="ntjjn9uvvowz">
          <CardTitle data-id="element-468" data-mp="nv4fr3wujfwy">Connection speed</CardTitle>
          <CardDescription data-id="element-469" data-mp="nwfj52mdqlnj">
            Throughput class, not the medium — fast WiFi also reports 4G
          </CardDescription>
        </CardHeader>
        <CardContent data-id="element-470" data-mp="e38gvqmxrlro">
          {connections.length ? <ShareBreakdown rows={toRows(connections)} data-id="element-471" data-mp="e2fh59me9wj1" /> : <EmptyHint data-id="element-472" data-mp="j2l44chpyuus" />}
          <Coverage share={coverage.connection} reason="Safari and Firefox don't implement the Network Information API" data-id="element-473" data-mp="j1s1nnpsmrig" />
        </CardContent>
      </Card>

      <Card data-id="element-474" data-mp="1bkfhoopxngco">
        <CardHeader data-id="element-475" data-mp="1bl8k5dhuzjp0">
          <CardTitle data-id="element-476" data-mp="1blgvv228wiym">WiFi vs cellular</CardTitle>
          <CardDescription data-id="element-477" data-mp="2w915ol6spxt5">The physical medium the visitor is on</CardDescription>
        </CardHeader>
        <CardContent data-id="element-478" data-mp="2r9wyu17cgc5z">
          {mediums.length ? <ShareBreakdown rows={toRows(mediums)} data-id="element-479" data-mp="2r8vknnv179k1" /> : <EmptyHint data-id="element-480" data-mp="352mk23f1td21" />}
          <Coverage share={coverage.medium} reason="only Chrome on Android reports the medium directly; elsewhere it is inferred from device and throughput, so some rows read “likely”" data-id="element-481" data-mp="35134yg54p81u" />
        </CardContent>
      </Card>
    </section>;
}